import { inject, Injectable, OnDestroy } from '@angular/core';
import { LoginState } from '@pxl/models/common';
import { LoginService, PlatformService, StatusBarService, ThemeService, UserService } from '@pxl/models/contracts';
import { firstValueFrom, ReplaySubject, Subscription } from 'rxjs';

@Injectable()
export class DefaultThemeService implements OnDestroy, ThemeService {
    public readonly onDarkThemeChange$ = new ReplaySubject<boolean>();

    private readonly prefersDarkColorSchemeMediaQueryList: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    private onUserLoginChangedSubscription: Subscription;
    private darkSchemeUserPreference: boolean | null = null;
    private systemDefaultIsDarkScheme: boolean;

    private readonly loginService = inject(LoginService);
    private readonly userService = inject(UserService);
    private readonly platformService = inject(PlatformService);
    private readonly statusBarService = inject(StatusBarService);

    public constructor() {
        this.systemDefaultIsDarkScheme = this.prefersDarkColorSchemeMediaQueryList.matches;

        if (this.prefersDarkColorSchemeMediaQueryList?.addEventListener) {
            this.prefersDarkColorSchemeMediaQueryList.addEventListener('change', this.onPrefersDarkColorSchemeChange);
        }
    }

    public get darkSchemePreference(): boolean {
        return this.darkSchemeUserPreference === null ? this.systemDefaultIsDarkScheme : this.darkSchemeUserPreference;
    }

    public init(): void {
        this.onUserLoginChangedSubscription = this.loginService.onUserLoginChanged().subscribe((value) => this.onUserLoginChanged(value));
    }

    public ngOnDestroy(): void {
        this.prefersDarkColorSchemeMediaQueryList.removeEventListener('change', this.onPrefersDarkColorSchemeChange);
        this.onUserLoginChangedSubscription.unsubscribe();
    }

    public setUserPreferenceForDarkScheme(userPreference: boolean | null | undefined): void {
        this.darkSchemeUserPreference = userPreference;
        this.applySchemeChanges();
    }

    private setSystemDefaultForDarkScheme(systemDefault: boolean): void {
        this.systemDefaultIsDarkScheme = systemDefault;
        this.applySchemeChanges();
    }

    private applySchemeChanges(): void {
        document.documentElement.classList.toggle('dark', this.darkSchemePreference);
        this.updateStatusBar();
        this.onDarkThemeChange$.next(this.darkSchemePreference);
    }

    private async onUserLoginChanged(value: LoginState): Promise<void> {
        let userPreferenceForDarkScheme: boolean | null = null;

        if (value.isLoggedIn && value.isTermsOfUseForUserValid) {
            const user = await firstValueFrom(this.userService.getUser());
            userPreferenceForDarkScheme = user.darkTheme;
        }

        this.setUserPreferenceForDarkScheme(userPreferenceForDarkScheme);
    }

    private readonly onPrefersDarkColorSchemeChange = (e: MediaQueryListEvent) => {
        this.setSystemDefaultForDarkScheme(e.matches);
    };

    private updateStatusBar(): void {
        if (this.platformService.isNativePlatform()) {
            if (this.darkSchemePreference) {
                this.statusBarService.setStatusBarStyleDark();
            } else {
                this.statusBarService.setStatusBarStyleLight();
            }
        }
    }
}
